import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  initializeSocket,
  disconnectSocket,
} from "../../../utils/socketHandler";
import axios from "axios";
import { setAccessToken, getAccessToken, clearAccessToken } from "../../../utils/tokenManager";
import { refreshTokenFromCookie, resetRefreshTokenFailure } from "../../../utils/getAuthToken";
import { invalidateAll } from "../../../utils/indexedDBCache";
import { clearCachedUserData } from "../../../utils/socketSharedState";
import { __resetLabelsCaches } from "../Contacts/labelsSlice";
import { __resetInboxInfoCaches } from "../Inbox/inboxInfoSlice";
import { __resetTeamMemberChatCaches } from "../Inbox/teamMemberChatSlice";
import { __resetInboxSettingsCaches } from "../Inbox/inboxSettingsSlice";
import { __resetWorkspaceCaches, requestWorkspacesList } from "../Workspace/workspaceSlice";
import { __resetInboxAllCaches } from "../Inbox/inboxAllSlice";
import { __resetUserDetailsCaches } from "../User/userDetailsSlice";
import { __resetDashboardCaches } from "../Dashboard/dashboardSlice";
import { __resetInboxRecentCaches } from "../Inbox/inboxRecentSlice";
import { __resetNotificationCaches } from "../Settings/notificationSlice";
import { __resetInboxMessageCaches } from "../Inbox/InboxMessageSlice";
import { getUserData, __resetUserDataSourceCache } from "../../../utils/userDataSource";

// Clears every module-level cache/debounce Map and the IndexedDB cache so a
// different account logging in on the same tab never sees stale data left
// over from the previous session (in-memory Redux state is separately reset
// by the root reducer in redux/store.js when this action fulfills).
const clearAllClientCaches = async () => {
  clearCachedUserData();
  __resetLabelsCaches();
  __resetInboxInfoCaches();
  __resetTeamMemberChatCaches();
  __resetInboxSettingsCaches();
  __resetWorkspaceCaches();
  __resetInboxAllCaches();
  __resetUserDetailsCaches();
  __resetDashboardCaches();
  __resetInboxRecentCaches();
  __resetNotificationCaches();
  __resetInboxMessageCaches();
  __resetUserDataSourceCache();
  await invalidateAll();
};

// API base URLs with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
const AUTH_API_URL = `${SERVER_DOMAIN}/api/auth`;
const API_URL = `${SERVER_DOMAIN}/api`;

// ===========================================================================
// Authentication Thunks
// ===========================================================================

// Thunk 1: Log in
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (userData, thunkAPI) => {
    try {
      const response = await axios.post(`${AUTH_API_URL}/login`, userData);

      // Add backward compatibility: ensure both token and accessToken exist
      const responseData = {
        ...response.data,
        token: response.data.accessToken, // Add legacy token property
      };

      return responseData;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { message: "Unable to connect to the server. Please try again." }
      );
    }
  }
);

// Thunk 1.5: Google Sign-In
export const googleLoginUser = createAsyncThunk(
  "auth/googleLoginUser",
  async (token, thunkAPI) => {
    try {
      const response = await axios.post(`${AUTH_API_URL}/google-signin`, { token });

      // Add backward compatibility: ensure both token and accessToken exist
      const responseData = {
        ...response.data,
        token: response.data.accessToken, // Add legacy token property
      };

      return responseData;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: "Google sign-in failed" });
    }
  }
);

// Thunk 1.6: Verify email (from the link sent at signup) and log in directly.
// Server issues a real session in one step - response shape matches loginUser's
// success payload exactly (accessToken, expiresIn, accountId, requiresOnboarding,
// etc.), except when the token was already used/invalid, in which case there's
// no accessToken at all (alreadyVerified:true) - handled in the reducer below.
export const verifyEmailAndLogin = createAsyncThunk(
  "auth/verifyEmailAndLogin",
  async (token, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/accounts/verify-email/${token}`);
      return {
        ...response.data,
        token: response.data.accessToken,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: "Unable to verify email at this time." });
    }
  }
);

// Thunk 2: Validate token (try refresh if needed)
export const validateToken = createAsyncThunk(
  "auth/validateToken",
  async (_, { rejectWithValue }) => {
    // 1) Check if we have access token in memory
    let token = getAccessToken();
    
    // 2) Check if we have a session indicator (just check if localStorage has user name)
    // This is just to know if there was a previous session - we don't use the data
    const storedUser = localStorage.getItem("user");
    
    // 3) If no token in memory, try to refresh using cookie (only if we have user data)
    // This prevents unnecessary refresh attempts when there's no session
    // Note: We don't restore tokens from localStorage for security (tokens aren't stored there)
    // Always refresh from cookie on page load to get a fresh, valid token
    if (!token) {
      if (storedUser) {
        // Only attempt refresh if we have user data (indicates a previous session)
        token = await refreshTokenFromCookie();
        if (token) {
          console.log('✅ Refreshed token from cookie on page load');
        } else {
          // Refresh failed - no valid session
          // Clear any stale user data
          localStorage.removeItem("user");
          return rejectWithValue("No valid session");
        }
      } else {
        // No token and no user data - no session exists
        return rejectWithValue("");
      }
    }
    
    // 4) Fetch user details and workspaces in parallel (skip redundant validate-token round-trip)
    // User details come from the shared userDataSource cache (same one userDetailsSlice.js
    // uses) so this doesn't fire its own separate real request every page load.
    const buildUserData = async (tok) => {
      const [userData, workspaces] = await Promise.all([
        getUserData(),
        requestWorkspacesList(tok),
      ]);
      // The access token carries the workspace the user actually switched to
      // (see workspaceController.switchWorkspace). Trust it over the account's
      // default workspace so validateToken() doesn't silently bounce the user
      // back to the default workspace on every page load/reload.
      const tokenWorkspace = workspaces.find(w => w._id === userData.workspaceId);
      const defaultWorkspace = workspaces.find(w => w.isDefault) || workspaces[0];
      const resolvedWorkspace = tokenWorkspace || defaultWorkspace;
      return {
        _id: userData._id,
        accountId: userData.AccountID,
        workspaceId: resolvedWorkspace?._id || null,
        name: userData.name,
        email: userData.email,
        workspaces: workspaces.map(ws => ({
          _id: ws._id,
          name: ws.name,
          isDefault: ws.isDefault,
        })),
        accessToken: tok,
      };
    };

    try {
      const fullUserData = await buildUserData(token);
      return { user: fullUserData };
    } catch (error) {
      // Token may have expired between the refresh and the profile fetch — try one more refresh
      if (error.response?.status === 401 && error.response?.data?.expired) {
        const refreshedToken = await refreshTokenFromCookie();
        if (refreshedToken) {
          try {
            const fullUserData = await buildUserData(refreshedToken);
            return { user: fullUserData };
          } catch (retryError) {
            return { user: { accessToken: refreshedToken } };
          }
        }
        localStorage.removeItem("user");
        return rejectWithValue("Token not valid");
      }
      return rejectWithValue(error.message);
    }
  }
);

// Thunk 4: Logout
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await axios.post(`${AUTH_API_URL}/logout`);
    } catch (error) {
      console.error("Logout error:", error);
      // Even if the server request fails, we still want to clear local storage
    }

    // Cleanup must never throw — logout has to always resolve as fulfilled
    // (same guarantee as before) so the store reset in redux/store.js runs.
    try {
      disconnectSocket();
    } catch (error) {
      console.error("Error disconnecting socket during logout:", error);
    }
    try {
      await clearAllClientCaches();
    } catch (error) {
      console.error("Error clearing client caches during logout:", error);
    }

    return { success: true };
  }
);

// Thunk: Validate account ID
export const validateAccountId = createAsyncThunk(
  "auth/validateAccountId",
  async (accountId, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage - we removed it for security)
      let token = getAccessToken();
      
      // If no token in memory, try to refresh using cookie
      if (!token) {
        token = await refreshTokenFromCookie();
        if (!token) {
          return rejectWithValue("No valid session - please login again");
        }
      }

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      const response = await axios.get(
        `${API_URL}/accounts/validate/${accountId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error validating account ID:", error);
      
      // If 401, try to refresh token and retry
      if (error.response?.status === 401 && error.response?.data?.expired) {
        const newToken = await refreshTokenFromCookie();
        if (newToken) {
          // Retry validation with new token
          try {
            const retryResponse = await axios.get(
              `${API_URL}/accounts/validate/${accountId}`,
              {
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              }
            );
            
            return retryResponse.data;
          } catch (retryError) {
            return rejectWithValue("Session expired - please login again");
          }
        } else {
          return rejectWithValue("Session expired - please login again");
        }
      }
      
      return rejectWithValue(
        error.response?.data?.message || "Failed to validate account ID"
      );
    }
  }
);

// ===========================================================================
// Auth Slice
// ===========================================================================

// Initial state
const initialState = {
  // Authentication state
  isLoading: false,
  user: null,
  isError: false,
  errorMessage: "",
  isAuthenticated: false,

  // Parameter validation state
  accountValidation: {
    isLoading: false,
    isValid: null,
    error: null,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetState(state) {
      // Reset state to initial values
      Object.assign(state, initialState);
      // Also clear localStorage if you want to log out
      localStorage.removeItem("user");
    },
    resetAccountValidation(state) {
      state.accountValidation = initialState.accountValidation;
    },
  },
  extraReducers: (builder) => {
    // ----------------------------------------------------
    // loginUser
    // ----------------------------------------------------
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = "";

        // Check if 2FA is required - if so, don't authenticate yet
        if (action.payload.requires2FA) {
          // Don't set authentication state, just store the email for OTP dialog
          state.user = null;
          state.isAuthenticated = false;
          return; // Exit early, don't process as successful login
        }

        // Store access token in memory (not localStorage - XSS protection)
        const accessToken = action.payload.accessToken || action.payload.token;
        const expiresIn = action.payload.expiresIn || 900; // 15 minutes default
        if (accessToken) {
          setAccessToken(accessToken, expiresIn);
          // Reset refresh token failure flag on successful login
          resetRefreshTokenFailure();
        }

        // Store full payload in Redux state (this is the source of truth for all user data)
        state.user = action.payload;
        state.isAuthenticated = true;

        // Store only minimal non-sensitive data in localStorage (name for display purposes)
        // All sensitive data (IDs, tokens, workspaces) should come from Redux state
        const safeUserData = {
          name: action.payload.name || action.payload.email || 'User'
        };
        localStorage.setItem("user", JSON.stringify(safeUserData));
        
        // Clear any old dummy/test data
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");

        // Ensure workspaceId is included in user object
        if (action.payload.workspaceId) {
          state.user.workspaceId = action.payload.workspaceId;
        }

        // Initialize socket with access token
        const userId = action.payload._id || action.payload.userId;
        if (userId && accessToken) {
          initializeSocket(userId, accessToken);
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isAuthenticated = false;
        state.user = null;

        // Ensure errorMessage is a string
        state.errorMessage =
          action.payload?.message ||
          "An unknown error occurred. Please try again.";
      })
      // ----------------------------------------------------
      // googleLoginUser
      // ----------------------------------------------------
      .addCase(googleLoginUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(googleLoginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = "";

        // Store access token in memory (not localStorage - XSS protection)
        const accessToken = action.payload.accessToken || action.payload.token;
        const expiresIn = action.payload.expiresIn || 900; // 15 minutes default
        if (accessToken) {
          setAccessToken(accessToken, expiresIn);
          // Reset refresh token failure flag on successful login
          resetRefreshTokenFailure();
        }

        // Store full payload in Redux state (this is the source of truth for all user data)
        state.user = action.payload;
        state.isAuthenticated = true;

        // Store only minimal non-sensitive data in localStorage (name for display purposes)
        // All sensitive data (IDs, tokens, workspaces) should come from Redux state
        const safeUserData = {
          name: action.payload.name || action.payload.email || 'User'
        };
        localStorage.setItem("user", JSON.stringify(safeUserData));
        
        // Clear any old dummy/test data
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");

        // Ensure workspaceId is included in user object
        if (action.payload.workspaceId) {
          state.user.workspaceId = action.payload.workspaceId;
        }

        // Initialize socket with access token
        const userId = action.payload._id || action.payload.userId;
        if (userId && accessToken) {
          initializeSocket(userId, accessToken);
        }
      })
      .addCase(googleLoginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isAuthenticated = false;
        state.user = null;

        // Ensure errorMessage is a string
        state.errorMessage =
          action.payload?.message ||
          "Google sign-in failed. Please try again.";
      })

      // ----------------------------------------------------
      // verifyEmailAndLogin
      // ----------------------------------------------------
      .addCase(verifyEmailAndLogin.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(verifyEmailAndLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = "";

        // Token already used/invalid - server treats this as "already
        // verified" rather than an error, but there's no session to log
        // into here. Leave auth state untouched; the page shows a message.
        const accessToken = action.payload.accessToken || action.payload.token;
        if (!accessToken) {
          return;
        }

        const expiresIn = action.payload.expiresIn || 900;
        setAccessToken(accessToken, expiresIn);
        resetRefreshTokenFailure();

        state.user = action.payload;
        state.isAuthenticated = true;

        const safeUserData = {
          name: action.payload.name || action.payload.email || 'User'
        };
        localStorage.setItem("user", JSON.stringify(safeUserData));
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");

        if (action.payload.workspaceId) {
          state.user.workspaceId = action.payload.workspaceId;
        }

        const userId = action.payload._id || action.payload.userId;
        if (userId && accessToken) {
          initializeSocket(userId, accessToken);
        }
      })
      .addCase(verifyEmailAndLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isAuthenticated = false;
        state.user = null;
        state.errorMessage =
          action.payload?.message ||
          "Unable to verify email at this time.";
      })

      // ----------------------------------------------------
      // validateToken
      // ----------------------------------------------------
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;

        // If valid, we get the user from action.payload.user
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(validateToken.rejected, (state, action) => {
        // Token validation failed, reset state
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        // Do not surface a global error on login page for token checks
        state.isError = false;
        state.errorMessage = "";
        // Also remove the stored user
        localStorage.removeItem("user");
      })
      // ----------------------------------------------------
      // logoutUser
      // ----------------------------------------------------
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        // Reset to initial state
        Object.assign(state, initialState);
        // Clear access token from memory
        clearAccessToken();
        // Clear user data from localStorage
        localStorage.removeItem("user");
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even on error, we still want to log out locally
        Object.assign(state, initialState);
        // Clear access token from memory
        clearAccessToken();
        // Clear user data from localStorage
        localStorage.removeItem("user");
      })

      // ----------------------------------------------------
      // validateAccountId
      // ----------------------------------------------------
      .addCase(validateAccountId.pending, (state) => {
        state.accountValidation.isLoading = true;
        state.accountValidation.error = null;
      })
      .addCase(validateAccountId.fulfilled, (state, action) => {
        state.accountValidation.isLoading = false;
        state.accountValidation.isValid = action.payload.valid;
        state.accountValidation.error = null;
      })
      .addCase(validateAccountId.rejected, (state, action) => {
        state.accountValidation.isLoading = false;
        state.accountValidation.isValid = false;
        state.accountValidation.error =
          action.payload || "Failed to validate account";
      });
  },
});

export const { resetState, resetAccountValidation } = authSlice.actions;
export default authSlice.reducer;
